/**
 * Accounting API service
 */
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, getValidAccessToken } from '../../../shared/api/axiosInstance';
import { API_BASE_URL } from '../../../shared/api/config';
import type { CurrencyRevaluation, ListFxRevaluationsParams } from '../types/fxRevaluation';
import {
  Account,
  AccountCreate,
  AccountUpdate,
  AccountBalanceReport,
  FiscalPeriod,
  FiscalPeriodCreate,
  GenerateFiscalYearRequest,
  JournalEntry,
  JournalEntryCreate,
  JournalEntryUpdate,
  JournalEntryReverse,
  Currency,
  CurrencyCreate,
  ExchangeRate,
  ExchangeRateCreate,
  FxReferenceRate,
  FxRatePolicy,
  FxRatePolicyListData,
  FxRatePolicyUpsert,
  FxDefaultMarkupUpdate,
  TrialBalanceReport,
  BalanceSheetReport,
  IncomeStatementReport,
  GeneralLedgerReport,
  PaginatedResponse,
  DataResponse,
  ListAccountsParams,
  ListJournalEntriesParams,
  ListFiscalPeriodsParams,
  FriendlyReportParams,
  InvoiceSummaryReport as InvoiceSummaryReportType,
  BillSummaryReport as BillSummaryReportType,
  ProfitLossReport as ProfitLossReportType,
  TrendReport as TrendReportData,
  TrendReportParams,
  AccountSuggestion,
  AdvancedReportParams,
  SummarizedIncomeReport,
  SummarizedExpensesReport,
  IncomeByCustomerReport,
  ExpensesByVendorReport,
  ExpensesAndPaymentsByVendorReport,
  CollectionReport,
  CollectionSummaryReport,
  ComparativePLReport,
  VATReport,
  WithholdingTaxCustomerReport,
  WithholdingTaxVendorReport,
  ExpenseWithholdingSummaryReport,
  PL6111Report,
  CashFlowStatementReport,
  CashFlowTimeseriesReport,
  CoaImportPreview,
  CoaImportResult,
  ChartStatus,
  Dimension,
  DimensionCreate,
  DimensionUpdate,
  DimensionValue,
  DimensionValueCreate,
  DimensionValueUpdate,
  DimensionDefaultRule,
  DimensionDefaultRuleCreate,
  DimensionDefaultRuleUpdate,
  ListDimensionDefaultRulesParams,
} from '../types';

const API_BASE = '/api/v1/accounting';

// File attachments (upload / list / delete / download) are served by the shared
// file_management seam — use `fileApi` from `shared/files/services/fileApi`
// (display via `useAuthenticatedImageUrl`, bytes via `fileApi.download` /
// `fileApi.fetchAsBlob`). The old module-local `fileApi`/`FileRecord` here
// bypassed that seam (raw authed `/download` URLs that 401 without a bearer) and
// have been removed.

// ============== AI Document Parsing ==============

export type DocumentType = 'bill' | 'receipt' | 'bill_and_receipt';
export type JeDetailLevel = 'summary' | 'grouped' | 'detailed';
/**
 * For doc_type='receipt' only.
 * - 'treat_as_paid_bill' (default): receipt is a fully-settled expense; finalize
 *   creates a paid-immediately Bill — JE: DR Expense / CR Bank.
 * - 'bill_will_follow': receipt is an advance/prepayment; finalize creates a
 *   Payment on account — JE: DR Accounts Payable / CR Bank. The vendor's AP
 *   carries a debit balance until a matching invoice arrives.
 */
export type ReceiptTreatment = 'treat_as_paid_bill' | 'bill_will_follow';

export const aiApi = {
  suggestAccount: async (
    companyName: string,
    accountType: 'expense' | 'revenue',
    companyIndustry?: string
  ): Promise<DataResponse<AccountSuggestion>> => {
    return apiPost(`${API_BASE}/suggest-account`, {
      company_name: companyName,
      account_type: accountType,
      company_industry: companyIndustry || undefined,
    });
  },
};

// ============== Accounts ==============

export interface AccountNumberSuggestion {
  suggested_number: string | null;
  range: { start: number; end: number };
  band: { start: number; end: number };
  categories: Array<{
    parent_account_id: string | null;
    // Stable key for a standard expense sub-category (frontend maps it to a
    // translated name); null for a tenant header account, whose `label` is its
    // own name and is displayed as-is (via BidiText).
    category_key: string | null;
    label: string;
    anchor: number;
    range_start: number;
    range_end: number;
  }>;
}

export interface ExpenseAccountSuggestionNewAccount {
  name: string;
  account_type: string;
  normal_balance: string;
  category: { range_start: number; range_end: number };
  sub_group_anchor: number | null;
  suggested_number: string | null;
  reasoning?: string | null;
}

export interface ExpenseAccountSuggestionExistingRef {
  account_id: string;
  account_number: string;
  name: string;
}

export type ExpenseAccountRecommendation =
  | {
      action: 'use_existing';
      existing: ExpenseAccountSuggestionExistingRef;
      reasoning?: string | null;
    }
  | { action: 'create_new'; new_account: ExpenseAccountSuggestionNewAccount };

export interface ExpenseAccountSuggestion {
  recommendation: ExpenseAccountRecommendation;
  alternatives: Array<{ action: 'create_new'; new_account: ExpenseAccountSuggestionNewAccount }>;
  close_existing: ExpenseAccountSuggestionExistingRef[];
}

export const accountApi = {
  list: async (params?: ListAccountsParams): Promise<PaginatedResponse<Account> | DataResponse<Account[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.account_type) searchParams.set('account_type', params.account_type);
    if (params?.is_active !== undefined) searchParams.set('is_active', String(params.is_active));
    if (params?.is_header !== undefined) searchParams.set('is_header', String(params.is_header));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.tree) searchParams.set('tree', 'true');
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/accounts${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Account> | DataResponse<Account[]>>(url);
  },

  get: async (id: string, includeBalance?: boolean): Promise<DataResponse<Account>> => {
    const searchParams = new URLSearchParams();
    if (includeBalance) searchParams.set('include_balance', 'true');

    const url = `${API_BASE}/accounts/${id}${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<Account>>(url);
  },

  create: async (data: AccountCreate): Promise<DataResponse<Account>> => {
    return apiPost<DataResponse<Account>>(`${API_BASE}/accounts`, data);
  },

  update: async (id: string, data: AccountUpdate): Promise<DataResponse<Account>> => {
    return apiPut<DataResponse<Account>>(`${API_BASE}/accounts/${id}`, data);
  },

  delete: async (id: string): Promise<void | DataResponse<Account>> => {
    // Note: This endpoint may return 204 or a deactivation response
    // Using apiDelete which doesn't return data, but the endpoint might return JSON
    await apiDelete(`${API_BASE}/accounts/${id}`);
  },

  initializeDefault: async (): Promise<DataResponse<{ account_count: number }>> => {
    return apiPost<DataResponse<{ account_count: number }>>(`${API_BASE}/accounts/initialize-default`);
  },

  getBalance: async (id: string, asOfDate?: string): Promise<DataResponse<AccountBalanceReport>> => {
    const searchParams = new URLSearchParams();
    if (asOfDate) searchParams.set('as_of_date', asOfDate);

    const url = `${API_BASE}/accounts/${id}/balance${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<AccountBalanceReport>>(url);
  },

  getTransactions: async (
    id: string,
    params?: { start_date?: string; end_date?: string; limit?: number; offset?: number }
  ): Promise<DataResponse<unknown[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/accounts/${id}/transactions${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<unknown[]>>(url);
  },

  getStatus: async (): Promise<DataResponse<ChartStatus>> => {
    return apiGet<DataResponse<ChartStatus>>(`${API_BASE}/accounts/status`);
  },

  // Deterministic (no-AI) next-number + category suggestion for the Create
  // Account dialog. Re-fetched when the account type / parent changes.
  numberSuggestion: async (params: {
    account_type: string;
    parent_account_id?: string | null;
    company_id?: string | null;
  }): Promise<DataResponse<AccountNumberSuggestion>> => {
    const searchParams = new URLSearchParams();
    searchParams.set('account_type', params.account_type);
    if (params.parent_account_id) searchParams.set('parent_account_id', params.parent_account_id);
    if (params.company_id) searchParams.set('company_id', params.company_id);
    return apiGet<DataResponse<AccountNumberSuggestion>>(
      `${API_BASE}/accounts/number-suggestion?${searchParams}`,
    );
  },

  // One-call AI expense-account suggestion for the smart Create Account dialog.
  suggestExpenseAccount: async (body: {
    description: string;
    vendor_name?: string | null;
    amount?: number | null;
    currency_code?: string | null;
    company_id?: string | null;
  }): Promise<DataResponse<ExpenseAccountSuggestion>> => {
    return apiPost<DataResponse<ExpenseAccountSuggestion>>(
      `${API_BASE}/accounts/suggest-expense-account`,
      body,
    );
  },

  initializeCustomized: async (data?: {
    business_models: Record<string, boolean>;
    industry_verticals: string[];
    business_type?: string;
  }): Promise<DataResponse<{ account_count: number; accounts_summary: Record<string, number> }>> => {
    return apiPost<DataResponse<{ account_count: number; accounts_summary: Record<string, number> }>>(
      `${API_BASE}/accounts/initialize`,
      data
    );
  },

  resetChart: async (): Promise<DataResponse<{
    deleted_count: number;
    retained_count: number;
    retained_accounts: Array<{ account_number: string; name: string }>;
  }>> => {
    return apiPost<DataResponse<{
      deleted_count: number;
      retained_count: number;
      retained_accounts: Array<{ account_number: string; name: string }>;
    }>>(`${API_BASE}/accounts/reset`);
  },

  resetAllChartData: async (stepUpProof: string): Promise<DataResponse<{
    deleted_count: number;
    deleted_draft_entry_count: number;
    retained_posted_entry_count: number;
  }>> => {
    return apiPost<DataResponse<{
      deleted_count: number;
      deleted_draft_entry_count: number;
      retained_posted_entry_count: number;
    }>>(`${API_BASE}/accounts/reset-all`, { step_up_proof: stepUpProof });
  },

  /**
   * Set the active company to inherit its parent's effective chart of accounts.
   * Creates no account rows — used by the "Use {parent}'s chart" setup action.
   */
  inherit: async (): Promise<DataResponse<ChartStatus>> => {
    return apiPost<DataResponse<ChartStatus>>(`${API_BASE}/accounts/inherit`);
  },

  exportFile: async (format: string, companyId?: string): Promise<Blob> => {
    const searchParams = new URLSearchParams({ format });
    if (companyId) searchParams.set('company_id', companyId);
    const token = await getValidAccessToken();
    const res = await fetch(`${API_BASE_URL}${API_BASE}/accounts/export?${searchParams}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to export accounts: ${res.statusText}`);
    return res.blob();
  },

  importPreview: async (file: File): Promise<DataResponse<CoaImportPreview>> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost<DataResponse<CoaImportPreview>>(`${API_BASE}/accounts/import/preview`, formData);
  },

  importFile: async (
    file: File,
    mappings: Record<string, string>,
    companyId?: string
  ): Promise<DataResponse<CoaImportResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    if (companyId) formData.append('company_id', companyId);
    return apiPost<DataResponse<CoaImportResult>>(`${API_BASE}/accounts/import`, formData);
  },
};

// ============== Opening AR/AP Balance Import ==============
// side='ar' → opening customer invoices; side='ap' → opening vendor bills. The
// preview/import responses share the generic tabular shape (CoaImport* types).

export type OpeningBalanceSide = 'ar' | 'ap';

export const openingBalanceApi = {
  importPreview: async (
    side: OpeningBalanceSide,
    file: File
  ): Promise<DataResponse<CoaImportPreview>> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost<DataResponse<CoaImportPreview>>(
      `${API_BASE}/opening-balances/${side}/import/preview`,
      formData
    );
  },

  importFile: async (
    side: OpeningBalanceSide,
    params: {
      file: File;
      mappings: Record<string, string>;
      cutoffDate: string;
      sourceSystem: string;
      companyId?: string;
    }
  ): Promise<DataResponse<CoaImportResult>> => {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('mappings', JSON.stringify(params.mappings));
    formData.append('cutoff_date', params.cutoffDate);
    formData.append('source_system', params.sourceSystem);
    if (params.companyId) formData.append('company_id', params.companyId);
    return apiPost<DataResponse<CoaImportResult>>(
      `${API_BASE}/opening-balances/${side}/import`,
      formData
    );
  },
};

// ============== Journal Entries ==============

export const journalEntryApi = {
  list: async (params?: ListJournalEntriesParams): Promise<PaginatedResponse<JournalEntry>> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.fiscal_period_id) searchParams.set('fiscal_period_id', params.fiscal_period_id);
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    if (params?.account_id) searchParams.set('account_id', params.account_id);
    if (params?.debit_account_id) searchParams.set('debit_account_id', params.debit_account_id);
    if (params?.credit_account_id) searchParams.set('credit_account_id', params.credit_account_id);
    if (params?.amount_min != null) searchParams.set('amount_min', String(params.amount_min));
    if (params?.amount_max != null) searchParams.set('amount_max', String(params.amount_max));
    if (params?.entry_type) searchParams.set('entry_type', params.entry_type);
    if (params?.created_by) searchParams.set('created_by', params.created_by);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/journal-entries${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<JournalEntry>>(url);
  },

  get: async (id: string): Promise<DataResponse<JournalEntry>> => {
    return apiGet<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries/${id}`);
  },

  create: async (data: JournalEntryCreate): Promise<DataResponse<JournalEntry>> => {
    return apiPost<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries`, data);
  },

  update: async (id: string, data: JournalEntryUpdate): Promise<DataResponse<JournalEntry>> => {
    return apiPut<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/journal-entries/${id}`);
  },

  post: async (id: string): Promise<DataResponse<JournalEntry>> => {
    return apiPost<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries/${id}/post`);
  },

  reverse: async (
    id: string,
    data: JournalEntryReverse,
  ): Promise<DataResponse<JournalEntry>> => {
    return apiPost<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries/${id}/reverse`, data);
  },
};

// ============== Fiscal Periods ==============

export const fiscalPeriodApi = {
  list: async (params?: ListFiscalPeriodsParams): Promise<PaginatedResponse<FiscalPeriod>> => {
    const searchParams = new URLSearchParams();
    if (params?.fiscal_year) searchParams.set('fiscal_year', String(params.fiscal_year));
    if (params?.period_type) searchParams.set('period_type', params.period_type);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.company_id) searchParams.set('company_id', params.company_id);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/fiscal-periods${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<FiscalPeriod>>(url);
  },

  get: async (id: string): Promise<DataResponse<FiscalPeriod>> => {
    return apiGet<DataResponse<FiscalPeriod>>(`${API_BASE}/fiscal-periods/${id}`);
  },

  create: async (data: FiscalPeriodCreate): Promise<DataResponse<FiscalPeriod>> => {
    return apiPost<DataResponse<FiscalPeriod>>(`${API_BASE}/fiscal-periods`, data);
  },

  generateYear: async (data: GenerateFiscalYearRequest): Promise<DataResponse<FiscalPeriod[]>> => {
    return apiPost<DataResponse<FiscalPeriod[]>>(`${API_BASE}/fiscal-periods/generate-year`, data);
  },

  close: async (id: string): Promise<DataResponse<FiscalPeriod>> => {
    return apiPost<DataResponse<FiscalPeriod>>(`${API_BASE}/fiscal-periods/${id}/close`);
  },

  reopen: async (id: string): Promise<DataResponse<FiscalPeriod>> => {
    return apiPost<DataResponse<FiscalPeriod>>(`${API_BASE}/fiscal-periods/${id}/reopen`);
  },

  lock: async (id: string): Promise<DataResponse<FiscalPeriod>> => {
    return apiPost<DataResponse<FiscalPeriod>>(`${API_BASE}/fiscal-periods/${id}/lock`);
  },
};

// ============== Currencies ==============

export const currencyApi = {
  list: async (isActive?: boolean): Promise<DataResponse<Currency[]>> => {
    const searchParams = new URLSearchParams();
    if (isActive !== undefined) searchParams.set('is_active', String(isActive));

    const url = `${API_BASE}/currencies${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<Currency[]>>(url);
  },

  create: async (data: CurrencyCreate): Promise<DataResponse<Currency>> => {
    return apiPost<DataResponse<Currency>>(`${API_BASE}/currencies`, data);
  },

  initializeDefault: async (): Promise<DataResponse<Currency[]>> => {
    return apiPost<DataResponse<Currency[]>>(`${API_BASE}/currencies/initialize-default`);
  },
};

// ============== Exchange Rates ==============

export const exchangeRateApi = {
  list: async (params?: {
    from_currency?: string;
    to_currency?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<DataResponse<ExchangeRate[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.from_currency) searchParams.set('from_currency', params.from_currency);
    if (params?.to_currency) searchParams.set('to_currency', params.to_currency);
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/exchange-rates${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<ExchangeRate[]>>(url);
  },

  create: async (data: ExchangeRateCreate): Promise<DataResponse<ExchangeRate>> => {
    return apiPost<DataResponse<ExchangeRate>>(`${API_BASE}/exchange-rates`, data);
  },

  delete: async (rateId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/exchange-rates/${rateId}`);
  },

  convert: async (params: {
    amount: number;
    from_currency: string;
    to_currency: string;
    rate_date?: string;
  }): Promise<DataResponse<{ amount: number; from_currency: string; to_currency: string; converted_amount: string; rate: string }>> => {
    const searchParams = new URLSearchParams();
    searchParams.set('amount', String(params.amount));
    searchParams.set('from_currency', params.from_currency);
    searchParams.set('to_currency', params.to_currency);
    if (params.rate_date) searchParams.set('rate_date', params.rate_date);

    const url = `${API_BASE}/exchange-rates/convert?${searchParams}`;
    return apiGet<DataResponse<{ amount: number; from_currency: string; to_currency: string; converted_amount: string; rate: string }>>(url);
  },
};

// ============== FX Reference Rates (global market rates, read-only) ==============

export const referenceRateApi = {
  list: async (params?: {
    base_currency?: string;
    quote_currency?: string;
    latest?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<DataResponse<FxReferenceRate[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.base_currency) searchParams.set('base_currency', params.base_currency);
    if (params?.quote_currency) searchParams.set('quote_currency', params.quote_currency);
    if (params?.latest !== undefined) searchParams.set('latest', String(params.latest));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/fx-reference-rates${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<FxReferenceRate[]>>(url);
  },
};

// ============== FX Rate Policies (one policy per directed currency pair) ==============

export const fxRatePolicyApi = {
  // Every pair the tenant tracks, merged with the live market rate, plus the
  // standing tenant-wide default markup that `default`-mode pairs inherit.
  list: async (): Promise<DataResponse<FxRatePolicyListData>> => {
    return apiGet<DataResponse<FxRatePolicyListData>>(`${API_BASE}/fx-rate-policies`);
  },

  // Set a pair's single mode (idempotent per pair). The backend clears whichever
  // store the chosen mode does not use, so exactly one rule governs the pair.
  upsert: async (data: FxRatePolicyUpsert): Promise<DataResponse<FxRatePolicy>> => {
    return apiPost<DataResponse<FxRatePolicy>>(`${API_BASE}/fx-rate-policies`, data);
  },

  // Stop tracking a pair: removes its markup row AND its pinned rates.
  delete: async (fromCurrency: string, toCurrency: string): Promise<void> => {
    await apiDelete(`${API_BASE}/fx-rate-policies/${fromCurrency}/${toCurrency}`);
  },

  setDefaultMarkup: async (
    data: FxDefaultMarkupUpdate,
  ): Promise<DataResponse<FxDefaultMarkupUpdate>> => {
    return apiPut<DataResponse<FxDefaultMarkupUpdate>>(
      `${API_BASE}/fx-rate-policies/default-markup`,
      data,
    );
  },
};

// ============== Reports ==============

export const reportApi = {
  getTrialBalance: async (params?: {
    as_of_date?: string;
    include_zero_balances?: boolean;
    consolidated?: boolean;
    reporting_currency?: string;
    dimension_value_id?: string;
  }): Promise<DataResponse<TrialBalanceReport>> => {
    const searchParams = new URLSearchParams();
    if (params?.as_of_date) searchParams.set('as_of_date', params.as_of_date);
    if (params?.include_zero_balances) searchParams.set('include_zero_balances', 'true');
    if (params?.consolidated) searchParams.set('consolidated', 'true');
    if (params?.reporting_currency) searchParams.set('reporting_currency', params.reporting_currency);
    if (params?.dimension_value_id) searchParams.set('dimension_value_id', params.dimension_value_id);

    const url = `${API_BASE}/reports/trial-balance${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<TrialBalanceReport>>(url);
  },

  getBalanceSheet: async (
    asOfDate?: string,
    options?: { consolidated?: boolean; reporting_currency?: string; period_start_date?: string },
  ): Promise<DataResponse<BalanceSheetReport>> => {
    const searchParams = new URLSearchParams();
    if (asOfDate) searchParams.set('as_of_date', asOfDate);
    if (options?.consolidated) searchParams.set('consolidated', 'true');
    if (options?.reporting_currency) searchParams.set('reporting_currency', options.reporting_currency);
    if (options?.period_start_date) searchParams.set('period_start_date', options.period_start_date);

    const url = `${API_BASE}/reports/balance-sheet${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<BalanceSheetReport>>(url);
  },

  getIncomeStatement: async (
    startDate: string,
    endDate: string,
    options?: { consolidated?: boolean; reporting_currency?: string },
  ): Promise<DataResponse<IncomeStatementReport>> => {
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', startDate);
    searchParams.set('end_date', endDate);
    if (options?.consolidated) searchParams.set('consolidated', 'true');
    if (options?.reporting_currency) searchParams.set('reporting_currency', options.reporting_currency);

    const url = `${API_BASE}/reports/income-statement?${searchParams}`;
    return apiGet<DataResponse<IncomeStatementReport>>(url);
  },

  getGeneralLedger: async (
    accountId: string,
    startDate: string,
    endDate: string,
    dimensionValueId?: string
  ): Promise<DataResponse<GeneralLedgerReport>> => {
    const searchParams = new URLSearchParams();
    searchParams.set('account_id', accountId);
    searchParams.set('start_date', startDate);
    searchParams.set('end_date', endDate);
    if (dimensionValueId) searchParams.set('dimension_value_id', dimensionValueId);

    const url = `${API_BASE}/reports/general-ledger?${searchParams}`;
    return apiGet<DataResponse<GeneralLedgerReport>>(url);
  },
};

// ============== Analytic Dimensions ==============

export interface ListDimensionsParams {
  include_inactive?: boolean;
  applies_to?: 'je' | 'bill';
  company_id?: string;
}

export const dimensionApi = {
  list: async (params?: ListDimensionsParams): Promise<PaginatedResponse<Dimension>> => {
    const searchParams = new URLSearchParams();
    if (params?.include_inactive) searchParams.set('include_inactive', 'true');
    if (params?.applies_to) searchParams.set('applies_to', params.applies_to);
    if (params?.company_id) searchParams.set('company_id', params.company_id);
    const qs = searchParams.toString();
    return apiGet<PaginatedResponse<Dimension>>(`${API_BASE}/dimensions${qs ? `?${qs}` : ''}`);
  },

  get: async (id: string): Promise<DataResponse<Dimension>> =>
    apiGet<DataResponse<Dimension>>(`${API_BASE}/dimensions/${id}`),

  create: async (data: DimensionCreate): Promise<DataResponse<Dimension>> =>
    apiPost<DataResponse<Dimension>>(`${API_BASE}/dimensions`, data),

  update: async (id: string, data: DimensionUpdate): Promise<DataResponse<Dimension>> =>
    apiPut<DataResponse<Dimension>>(`${API_BASE}/dimensions/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/dimensions/${id}`);
  },

  listValues: async (
    dimensionId: string,
    includeInactive?: boolean,
  ): Promise<PaginatedResponse<DimensionValue>> => {
    const qs = includeInactive ? '?include_inactive=true' : '';
    return apiGet<PaginatedResponse<DimensionValue>>(
      `${API_BASE}/dimensions/${dimensionId}/values${qs}`,
    );
  },

  createValue: async (
    dimensionId: string,
    data: DimensionValueCreate,
  ): Promise<DataResponse<DimensionValue>> =>
    apiPost<DataResponse<DimensionValue>>(`${API_BASE}/dimensions/${dimensionId}/values`, data),

  updateValue: async (
    valueId: string,
    data: DimensionValueUpdate,
  ): Promise<DataResponse<DimensionValue>> =>
    apiPut<DataResponse<DimensionValue>>(`${API_BASE}/dimension-values/${valueId}`, data),

  deleteValue: async (valueId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/dimension-values/${valueId}`);
  },
};

// ============== Default-Dimension Rules (P9) ==============

export const dimensionDefaultRuleApi = {
  list: async (
    params?: ListDimensionDefaultRulesParams,
  ): Promise<PaginatedResponse<DimensionDefaultRule>> => {
    const searchParams = new URLSearchParams();
    if (params?.company_id) searchParams.set('company_id', params.company_id);
    if (params?.include_inactive) searchParams.set('include_inactive', 'true');
    const qs = searchParams.toString();
    return apiGet<PaginatedResponse<DimensionDefaultRule>>(
      `${API_BASE}/dimension-default-rules${qs ? `?${qs}` : ''}`,
    );
  },

  get: async (id: string): Promise<DataResponse<DimensionDefaultRule>> =>
    apiGet<DataResponse<DimensionDefaultRule>>(`${API_BASE}/dimension-default-rules/${id}`),

  create: async (data: DimensionDefaultRuleCreate): Promise<DataResponse<DimensionDefaultRule>> =>
    apiPost<DataResponse<DimensionDefaultRule>>(`${API_BASE}/dimension-default-rules`, data),

  update: async (
    id: string,
    data: DimensionDefaultRuleUpdate,
  ): Promise<DataResponse<DimensionDefaultRule>> =>
    apiPut<DataResponse<DimensionDefaultRule>>(`${API_BASE}/dimension-default-rules/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/dimension-default-rules/${id}`);
  },
};

// ============== FX Revaluation ==============

export const fxRevaluationApi = {
  list: async (
    params?: ListFxRevaluationsParams,
  ): Promise<DataResponse<CurrencyRevaluation[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.company_id) searchParams.set('company_id', params.company_id);
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    if (params?.offset != null) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return apiGet<DataResponse<CurrencyRevaluation[]>>(
      `${API_BASE}/fx-revaluations${qs ? `?${qs}` : ''}`,
    );
  },

  get: async (id: string): Promise<DataResponse<CurrencyRevaluation>> =>
    apiGet<DataResponse<CurrencyRevaluation>>(`${API_BASE}/fx-revaluations/${id}`),

  run: async (fiscalPeriodId: string): Promise<DataResponse<CurrencyRevaluation>> =>
    apiPost<DataResponse<CurrencyRevaluation>>(`${API_BASE}/fx-revaluations`, {
      fiscal_period_id: fiscalPeriodId,
    }),
};

// ============== Friendly Reports ==============

export const friendlyReportApi = {
  getInvoiceSummary: async (params: FriendlyReportParams): Promise<DataResponse<InvoiceSummaryReportType>> => {
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    if (params.group_by) searchParams.set('group_by', params.group_by);
    return apiGet<DataResponse<InvoiceSummaryReportType>>(`${API_BASE}/friendly-reports/invoice-summary?${searchParams}`);
  },

  getBillSummary: async (params: FriendlyReportParams): Promise<DataResponse<BillSummaryReportType>> => {
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    if (params.group_by) searchParams.set('group_by', params.group_by);
    return apiGet<DataResponse<BillSummaryReportType>>(`${API_BASE}/friendly-reports/bill-summary?${searchParams}`);
  },

  getProfitLoss: async (params: FriendlyReportParams): Promise<DataResponse<ProfitLossReportType>> => {
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    if (params.group_by) searchParams.set('group_by', params.group_by);
    return apiGet<DataResponse<ProfitLossReportType>>(`${API_BASE}/friendly-reports/profit-loss?${searchParams}`);
  },

  getTrend: async (params: TrendReportParams): Promise<DataResponse<TrendReportData>> => {
    const searchParams = new URLSearchParams();
    searchParams.set('report', params.report);
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    if (params.group_by) searchParams.set('group_by', params.group_by);
    if (params.dimension) searchParams.set('dimension', params.dimension);
    if (params.top_n != null) searchParams.set('top_n', String(params.top_n));
    return apiGet<DataResponse<TrendReportData>>(`${API_BASE}/friendly-reports/trend?${searchParams}`);
  },
};

// ============== Advanced Reports ==============

function dateParams(p: AdvancedReportParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('start_date', p.start_date);
  sp.set('end_date', p.end_date);
  return sp;
}

export const advancedReportApi = {
  getSummarizedIncome: async (params: AdvancedReportParams): Promise<DataResponse<SummarizedIncomeReport>> =>
    apiGet(`${API_BASE}/reports/summarized-income?${dateParams(params)}`),

  getSummarizedExpenses: async (params: AdvancedReportParams): Promise<DataResponse<SummarizedExpensesReport>> =>
    apiGet(`${API_BASE}/reports/summarized-expenses?${dateParams(params)}`),

  getIncomeByCustomer: async (params: AdvancedReportParams): Promise<DataResponse<IncomeByCustomerReport>> =>
    apiGet(`${API_BASE}/reports/income-by-customer?${dateParams(params)}`),

  getExpensesByVendor: async (params: AdvancedReportParams): Promise<DataResponse<ExpensesByVendorReport>> =>
    apiGet(`${API_BASE}/reports/expenses-by-vendor?${dateParams(params)}`),

  getExpensesAndPaymentsByVendor: async (params: AdvancedReportParams): Promise<DataResponse<ExpensesAndPaymentsByVendorReport>> =>
    apiGet(`${API_BASE}/reports/expenses-and-payments-by-vendor?${dateParams(params)}`),

  getCollection: async (params: AdvancedReportParams & { customer_id?: string; document_type?: string }): Promise<DataResponse<CollectionReport>> => {
    const sp = dateParams(params);
    if (params.customer_id) sp.set('customer_id', params.customer_id);
    if (params.document_type) sp.set('document_type', params.document_type);
    return apiGet(`${API_BASE}/reports/collection?${sp}`);
  },

  getCollectionSummary: async (params: AdvancedReportParams): Promise<DataResponse<CollectionSummaryReport>> => {
    const sp = dateParams(params);
    sp.set('show_summary_only', 'true');
    return apiGet(`${API_BASE}/reports/collection?${sp}`);
  },

  getComparativePL: async (params: { mode: string; year?: number; years?: number[] }): Promise<DataResponse<ComparativePLReport>> => {
    const sp = new URLSearchParams();
    sp.set('mode', params.mode);
    if (params.year) sp.set('year', params.year.toString());
    if (params.years) sp.set('years', params.years.join(','));
    return apiGet(`${API_BASE}/reports/comparative-profit-loss?${sp}`);
  },

  getVAT: async (params: AdvancedReportParams): Promise<DataResponse<VATReport>> =>
    apiGet(`${API_BASE}/reports/vat?${dateParams(params)}`),

  getWithholdingTaxCustomers: async (params: AdvancedReportParams): Promise<DataResponse<WithholdingTaxCustomerReport>> =>
    apiGet(`${API_BASE}/reports/withholding-tax-customers?${dateParams(params)}`),

  getWithholdingTaxVendors: async (params: AdvancedReportParams): Promise<DataResponse<WithholdingTaxVendorReport>> =>
    apiGet(`${API_BASE}/reports/withholding-tax-vendors?${dateParams(params)}`),

  getExpenseWithholdingSummary: async (params: AdvancedReportParams): Promise<DataResponse<ExpenseWithholdingSummaryReport>> =>
    apiGet(`${API_BASE}/reports/expense-withholding-summary?${dateParams(params)}`),

  getPL6111: async (year: number): Promise<DataResponse<PL6111Report>> =>
    apiGet(`${API_BASE}/reports/profit-loss-6111?year=${year}`),

  getCashFlow: async (params: AdvancedReportParams): Promise<DataResponse<CashFlowStatementReport>> =>
    apiGet(`${API_BASE}/reports/cash-flow?${dateParams(params)}`),

  getCashFlowTimeseries: async (params: AdvancedReportParams): Promise<DataResponse<CashFlowTimeseriesReport>> =>
    apiGet(`${API_BASE}/reports/cash-flow-timeseries?${dateParams(params)}`),

  exportReport: (reportName: string, format: 'pdf' | 'xlsx' | 'csv', params: Record<string, string>): string => {
    const sp = new URLSearchParams(params);
    sp.set('format', format);
    return `${API_BASE}/reports/${reportName}/export?${sp}`;
  },
};

// ============== Document Ingestion ==============

const INGESTION_BASE = '/api/v1/accounting/ingestion';

export interface OrganizationSearchResult {
  id: string;
  name: string;
  domain: string | null;
  aliases: string[];
  tax_id: string | null;
  match_score: number;
}

export const ingestionApi = {
  createRule: (data: IngestionRuleCreate) =>
    apiPost<DataResponse<IngestionRule>>(`${INGESTION_BASE}/rules`, data),

  listRules: (params?: { is_active?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.is_active !== undefined) searchParams.set('is_active', String(params.is_active));
    const qs = searchParams.toString();
    return apiGet<{ data: IngestionRule[] }>(`${INGESTION_BASE}/rules${qs ? `?${qs}` : ''}`);
  },

  updateRule: (ruleId: string, data: Partial<IngestionRuleCreate> & { is_active?: boolean }) =>
    apiPatch<DataResponse<IngestionRule>>(`${INGESTION_BASE}/rules/${ruleId}`, data),

  deleteRule: (ruleId: string) =>
    apiDelete(`${INGESTION_BASE}/rules/${ruleId}`),
};

// ============== Ingestion Rules ==============

export type IngestionRuleConditionField =
  | 'payment_card_last4'
  | 'payment_method'
  | 'vendor_name'
  | 'description'
  | 'currency'
  | 'amount'
  | 'document_type';

export type IngestionRuleConditionOperator =
  | 'equals'
  | 'contains'
  | 'greater_than'
  | 'less_than';

export type IngestionRuleSource = 'user' | 'suggested' | 'auto_learned';

export interface IngestionRuleCondition {
  field: IngestionRuleConditionField;
  operator: IngestionRuleConditionOperator;
  value: string | number;
}

export interface IngestionRuleActions {
  set_payment_account_id?: string;
  set_account_id?: string;
  set_tax_rate_id?: string;
  set_document_type?: DocumentType;
  set_je_detail_level?: JeDetailLevel;
}

/**
 * A rule's actions as STORED. `set_company_id` (assign the vendor organization)
 * was retired in Phase 5a of the learned-match design — identity is single-sourced
 * to Tier 1 — so it is no longer authorable and the backend rejects it on write.
 * Rows created before that still carry the key: it is inert, and the edit dialog
 * strips it so a re-save cleans it up instead of failing validation.
 */
export interface StoredIngestionRuleActions extends IngestionRuleActions {
  set_company_id?: string;
}

export interface IngestionRule {
  id: string;
  name: string;
  description: string | null;
  conditions: IngestionRuleCondition[];
  actions: IngestionRuleActions;
  priority: number;
  auto_approve: boolean;
  source: IngestionRuleSource;
  times_applied: number;
  is_active: boolean;
}

export interface IngestionRuleCreate {
  name: string;
  conditions: IngestionRuleCondition[];
  actions: IngestionRuleActions;
  description?: string;
  priority?: number;
  auto_approve?: boolean;
  /** When set, re-apply the new rule across the still-unmatched items in this open batch. */
  batch_id?: string;
}

// ============== Revenue Category Rules ==============

export interface RevenueCategoryRule {
  id: string;
  tenant_id: string;
  account_id: string;
  entity_type: 'product' | 'product_category' | 'customer';
  entity_id: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const revenueCategoryApi = {
  list: (params?: { entity_type?: string; account_id?: string }): Promise<{ data: RevenueCategoryRule[] }> => {
    const searchParams = new URLSearchParams();
    if (params?.entity_type) searchParams.set('entity_type', params.entity_type);
    if (params?.account_id) searchParams.set('account_id', params.account_id);
    return apiGet(`${API_BASE}/revenue-category-rules${searchParams.toString() ? `?${searchParams}` : ''}`);
  },

  create: (data: { account_id: string; entity_type: string; entity_id: string }): Promise<{ data: RevenueCategoryRule }> => {
    return apiPost(`${API_BASE}/revenue-category-rules`, data);
  },

  bulkUpsert: (data: { account_id: string; entity_type: string; entity_ids: string[] }): Promise<{ data: RevenueCategoryRule[] }> => {
    return apiPost(`${API_BASE}/revenue-category-rules/bulk`, data);
  },

  delete: (ruleId: string): Promise<void> => {
    return apiDelete(`${API_BASE}/revenue-category-rules/${ruleId}`);
  },

  resolve: (data: {
    product_id?: string;
    product_category_id?: string;
    invoice_level_account_id?: string;
    customer_id?: string;
  }): Promise<{ data: { account_id: string | null } }> => {
    return apiPost(`${API_BASE}/revenue-category-rules/resolve`, data);
  },
};

// ============== Expense Category Rules ==============

export interface ExpenseCategoryRule {
  id: string;
  tenant_id: string;
  account_id: string;
  entity_type: 'product' | 'product_category' | 'vendor';
  entity_id: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const expenseCategoryApi = {
  list: (params?: { entity_type?: string; account_id?: string }): Promise<{ data: ExpenseCategoryRule[] }> => {
    const searchParams = new URLSearchParams();
    if (params?.entity_type) searchParams.set('entity_type', params.entity_type);
    if (params?.account_id) searchParams.set('account_id', params.account_id);
    return apiGet(`${API_BASE}/expense-category-rules${searchParams.toString() ? `?${searchParams}` : ''}`);
  },

  create: (data: { account_id: string; entity_type: string; entity_id: string }): Promise<{ data: ExpenseCategoryRule }> => {
    return apiPost(`${API_BASE}/expense-category-rules`, data);
  },

  bulkUpsert: (data: { account_id: string; entity_type: string; entity_ids: string[] }): Promise<{ data: ExpenseCategoryRule[] }> => {
    return apiPost(`${API_BASE}/expense-category-rules/bulk`, data);
  },

  delete: (ruleId: string): Promise<void> => {
    return apiDelete(`${API_BASE}/expense-category-rules/${ruleId}`);
  },

  resolve: (data: {
    product_id?: string;
    product_category_id?: string;
    bill_level_account_id?: string;
    vendor_id?: string;
  }): Promise<{ data: { account_id: string | null } }> => {
    return apiPost(`${API_BASE}/expense-category-rules/resolve`, data);
  },
};

// ============== Organization Fuzzy Search (from contacts) ==============

const CONTACTS_BASE = '/api/v1/master-data/contacts';

export const organizationSearchApi = {
  fuzzySearch: (query: string, limit = 10) => {
    const searchParams = new URLSearchParams();
    searchParams.set('q', query);
    searchParams.set('limit', String(limit));
    return apiGet<{ data: OrganizationSearchResult[] }>(`${CONTACTS_BASE}/organizations/fuzzy-search?${searchParams}`);
  },
};
